import React, { useState, useEffect } from 'react';
import { AdminShareMetaData, AdminSharesListQuery, AdminSharesListResponse, ShareHistoryItem, Pagination } from '@df-gate-share/shared';
import { apiClient } from '../../lib/api';
import MessageDialog from '../../components/MessageDialog';
import { useMessageDialog } from '../../hooks/useMessageDialog';

type StatusFilter = 'all' | 'active' | 'expired' | 'revoked';

const ShareHistory: React.FC = () => {
  const { dialogState, closeDialog, showAlert } = useMessageDialog();
  const [shares, setShares] = useState<AdminShareMetaData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [filters, setFilters] = useState({
    senderEmail: '',
    status: 'all' as StatusFilter
  });
  const [pagination, setPagination] = useState<Pagination>({
    hasMore: false,
    count: 0
  });
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
  const [selectedShare, setSelectedShare] = useState<AdminShareMetaData | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState<AdminShareMetaData | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState<AdminShareMetaData | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  // CR-045: 確定系操作の二重送信ガード
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchShares();
  }, []);

  const fetchShares = async (cursor?: string, resetHistory: boolean = false) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      if (filters.senderEmail) params.append('senderEmail', filters.senderEmail);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (cursor) params.append('cursor', cursor);
      params.append('limit', '50');

      const response = await apiClient.get(`/api/v1/admin/shares?${params.toString()}`);
      const data: AdminSharesListResponse = response.data;
      setShares(data.shares || []);
      setPagination(data.pagination || { hasMore: false, count: 0 });

      if (resetHistory) {
        setCursorHistory([]);
      }
    } catch (err) {
      setError('共有履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasMore && pagination.nextCursor) {
      setCursorHistory([...cursorHistory, currentCursor]);
      setCurrentCursor(pagination.nextCursor);
      fetchShares(pagination.nextCursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const prevCursor = newHistory.pop();
      setCursorHistory(newHistory);
      setCurrentCursor(prevCursor);
      fetchShares(prevCursor);
    }
  };

  const handleSearch = () => {
    setCurrentCursor(undefined);
    setCursorHistory([]);
    fetchShares(undefined, true);
  };

  const handleRevokeShare = async () => {
    if (!showRevokeDialog || !revokeReason.trim()) {
      showAlert('warning', '入力エラー', '無効化の理由を入力してください');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await apiClient.put(`/api/v1/admin/shares/${showRevokeDialog.shareId}/revoke`, {
        reason: revokeReason
      });
      setShowRevokeDialog(null);
      setRevokeReason('');
      fetchShares();
      showAlert('success', '完了', '共有リンクを無効化しました。オーナーにメール通知が送信されます。');
    } catch (err) {
      showAlert('error', 'エラー', '共有リンクの無効化に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmergencyDelete = async () => {
    if (!showDeleteDialog || !deleteReason.trim()) {
      showAlert('warning', '入力エラー', '削除の理由を入力してください');
      return;
    }

    const fileIds = showDeleteDialog.fileIds || [];
    if (fileIds.length === 0) {
      showAlert('warning', '入力エラー', '削除するファイルがありません');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      // Delete all files
      const deletePromises = fileIds.map(fileId =>
        apiClient.delete(`/api/v1/admin/files/${fileId}`, {
          data: { reason: deleteReason }
        })
      );

      await Promise.all(deletePromises);

      const senderEmail = showDeleteDialog.senderEmail;
      setShowDeleteDialog(null);
      setDeleteReason('');
      fetchShares();
      showAlert('success', '完了',
        `${fileIds.length}個のファイルを削除しました。\nS3から即座に削除され、オーナー（${senderEmail}）にメール通知が送信されます。`
      );
    } catch (err) {
      showAlert('error', 'エラー', 'ファイルの削除に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.senderEmail) params.append('senderEmail', filters.senderEmail);
      if (filters.status !== 'all') params.append('status', filters.status);
      params.append('format', 'csv');

      const response = await apiClient.get(`/api/v1/admin/shares?${params.toString()}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `share-history-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showAlert('error', 'エラー', 'CSVエクスポートに失敗しました');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isRecent = (createdAt: string): boolean => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
    return diffHours <= 24;
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'active': return 'badge badge--active';
      case 'expired': return 'badge badge--expired';
      default: return 'badge badge--danger';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'active': return '有効';
      case 'expired': return '期限切れ';
      default: return '無効化済み';
    }
  };

  return (
    <div className="card card__body">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="page-title">共有履歴管理</h1>
        <button
          onClick={handleExportCSV}
          className="btn btn--success"
        >
          <span className="material-symbols-outlined icon-sm">bar_chart</span> CSVエクスポート
        </button>
      </div>

      {/* Filters */}
      <div className="card card__body mb-6" style={{ background: 'var(--color-bg)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
          <div>
            <label className="form-label">送信者メールアドレス</label>
            <input
              type="email"
              value={filters.senderEmail}
              onChange={(e) => setFilters({ ...filters, senderEmail: e.target.value })}
              placeholder="sender@example.com"
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">ステータス</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as StatusFilter })}
              className="form-select"
            >
              <option value="all">すべて</option>
              <option value="active">有効</option>
              <option value="expired">期限切れ</option>
              <option value="revoked">無効化済み</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSearch}
            className="btn btn--primary"
          >
            <span className="material-symbols-outlined icon-sm">search</span> 検索
          </button>
          <button
            onClick={() => {
              setFilters({
                senderEmail: '',
                status: 'all'
              });
              setCurrentCursor(undefined);
              setCursorHistory([]);
            }}
            className="btn btn--secondary"
          >
            リセット
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert--error">{error}</div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center text-muted" style={{ padding: 'var(--space-10)' }}>
          読み込み中...
        </div>
      ) : (
        <>
          {/* Shares Table */}
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>送信日時</th>
                  <th>タイトル</th>
                  <th>送信者</th>
                  <th>受信者数</th>
                  <th>ファイル数</th>
                  <th>サイズ</th>
                  <th>ステータス</th>
                  <th>DL数</th>
                  <th style={{ textAlign: 'center' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {shares.map((share) => (
                  <tr
                    key={share.shareId}
                    style={{
                      backgroundColor: isRecent(share.createdAt) ? 'var(--color-warning-bg)' : undefined
                    }}
                  >
                    <td>
                      {formatDate(share.createdAt)}
                      {isRecent(share.createdAt) && (
                        <span className="badge badge--new" style={{ marginLeft: 'var(--space-2)' }}>
                          NEW
                        </span>
                      )}
                    </td>
                    <td style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                      {share.title}
                    </td>
                    <td>{share.senderEmail}</td>
                    <td>{share.recipients.length}人</td>
                    <td>{share.fileCount}個</td>
                    <td>{formatFileSize(share.totalSize)}</td>
                    <td>
                      <span className={getStatusBadgeClass(share.status)}>
                        {getStatusLabel(share.status)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>{share.downloadCount}回</td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="flex flex-col gap-1 items-center" style={{ minWidth: '100px' }}>
                        <button
                          onClick={() => {
                            setSelectedShare(share);
                            setShowDetailModal(true);
                          }}
                          className="btn btn--sm btn--primary"
                          style={{ width: '100%' }}
                          title="詳細を表示"
                        >
                          <span className="material-symbols-outlined icon-sm">description</span> 詳細
                        </button>
                        {share.status === 'active' && (
                          <>
                            <button
                              onClick={() => setShowRevokeDialog(share)}
                              className="btn btn--sm btn--danger"
                              style={{ width: '100%' }}
                              title="共有を無効化"
                            >
                              <span className="material-symbols-outlined icon-sm">block</span> 無効化
                            </button>
                            <button
                              onClick={() => setShowDeleteDialog(share)}
                              className="btn btn--sm btn--danger font-bold"
                              style={{ width: '100%' }}
                              title="ファイルを緊急削除"
                            >
                              <span className="material-symbols-outlined icon-sm">emergency</span> 緊急削除
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {shares.length === 0 && (
              <div className="empty-state">
                <div className="empty-state__desc">共有履歴が見つかりません</div>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {shares.length > 0 && (
            <div className="pagination">
              <div className="pagination__info">
                {pagination.count}件の結果を表示中
              </div>
              <div className="pagination__buttons">
                <button
                  onClick={handlePrevPage}
                  disabled={cursorHistory.length === 0}
                  className="btn btn--primary btn--sm"
                >
                  ← 前へ
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore}
                  className="btn btn--primary btn--sm"
                >
                  次へ →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedShare && (
        <div className="modal-overlay">
          <div className="modal modal--wide">
            <h2 className="modal__title">共有詳細</h2>

            <div className="mb-6">
              <div className="mb-4">
                <div className="text-xs text-muted mb-2">タイトル</div>
                <div className="font-semibold" style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-primary)' }}>{selectedShare.title}</div>
              </div>

              <div className="mb-4">
                <div className="text-xs text-muted mb-2">送信者</div>
                <div style={{ color: 'var(--color-text-primary)' }}>{selectedShare.senderEmail}</div>
              </div>

              <div className="mb-4">
                <div className="text-xs text-muted mb-2">受信者</div>
                <div style={{ color: 'var(--color-text-primary)' }}>
                  {selectedShare.recipients.map((recipient, index) => (
                    <div key={index} style={{ marginBottom: 'var(--space-1)' }}>• {recipient}</div>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <div className="text-xs text-muted mb-2">ファイル</div>
                <div style={{ color: 'var(--color-text-primary)' }}>
                  {selectedShare.fileCount}個のファイル
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <div>
                  <div className="text-xs text-muted mb-2">ステータス</div>
                  <div>
                    <span className={getStatusBadgeClass(selectedShare.status)}>
                      {getStatusLabel(selectedShare.status)}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-muted mb-2">送信日時</div>
                  <div style={{ color: 'var(--color-text-primary)' }}>{formatDate(selectedShare.createdAt)}</div>
                </div>

                <div>
                  <div className="text-xs text-muted mb-2">有効期限</div>
                  <div style={{ color: 'var(--color-text-primary)' }}>{formatDate(selectedShare.expiresAt)}</div>
                </div>

                {selectedShare.expiredAt && (
                  <div>
                    <div className="text-xs text-muted mb-2">期限切れ日時</div>
                    <div style={{ color: 'var(--color-warning)' }}>{formatDate(selectedShare.expiredAt)}</div>
                  </div>
                )}

                {selectedShare.filesDeletedAt && (
                  <div>
                    <div className="text-xs text-muted mb-2">ファイル削除日時</div>
                    <div style={{ color: 'var(--color-danger)' }}>{formatDate(selectedShare.filesDeletedAt)}</div>
                  </div>
                )}

                <div>
                  <div className="text-xs text-muted mb-2">ダウンロード回数</div>
                  <div style={{ color: 'var(--color-text-primary)' }}>{selectedShare.downloadCount}回</div>
                </div>

                {selectedShare.lastDownloadAt && (
                  <div>
                    <div className="text-xs text-muted mb-2">最終ダウンロード</div>
                    <div style={{ color: 'var(--color-text-primary)' }}>{formatDate(selectedShare.lastDownloadAt)}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal__actions">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedShare(null);
                }}
                className="btn btn--secondary"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation Dialog */}
      {showRevokeDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title" style={{ color: 'var(--color-danger)' }}>
              <span className="material-symbols-outlined icon-sm">warning</span> 共有リンクを無効化
            </h2>
            <p className="text-muted mb-6" style={{ fontSize: 'var(--text-base)' }}>
              共有「{showRevokeDialog.title}」を無効化します。<br />
              この操作は取り消せません。オーナー（{showRevokeDialog.senderEmail}）にメール通知が送信されます。
            </p>

            <div className="form-group">
              <label className="form-label form-label--required">無効化の理由</label>
              <textarea
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="例: 誤送信のため、オーナーからの要請により、など"
                rows={4}
                className="form-textarea"
              />
            </div>

            <div className="modal__actions">
              <button
                onClick={() => {
                  setShowRevokeDialog(null);
                  setRevokeReason('');
                }}
                className="btn btn--secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleRevokeShare}
                disabled={!revokeReason.trim() || isSubmitting}
                className="btn btn--danger"
              >
                {isSubmitting ? '無効化中...' : '無効化する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emergency Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title" style={{ color: 'var(--color-danger)' }}>
              <span className="material-symbols-outlined icon-sm">warning</span> ファイルを緊急削除
            </h2>
            <p className="text-muted mb-6" style={{ fontSize: 'var(--text-base)' }}>
              共有「{showDeleteDialog.title}」の全ファイル（{showDeleteDialog.fileCount}個）を即座に削除します。<br />
              <strong style={{ color: 'var(--color-danger)' }}>この操作は取り消せません。</strong><br />
              S3から即座に削除され、オーナー（{showDeleteDialog.senderEmail}）にメール通知が送信されます。
            </p>

            <div className="info-box info-box--warning mb-6">
              <div className="font-semibold mb-2" style={{ fontSize: 'var(--text-sm)' }}>削除されるファイル:</div>
              <div className="text-xs">
                {showDeleteDialog.fileCount}個のファイルが削除されます
              </div>
            </div>

            <div className="form-group">
              <label className="form-label form-label--required">削除の理由</label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="例: 誤送信のため、機密情報漏洩の恐れ、など"
                rows={4}
                className="form-textarea"
              />
            </div>

            <div className="modal__actions">
              <button
                onClick={() => {
                  setShowDeleteDialog(null);
                  setDeleteReason('');
                }}
                className="btn btn--secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleEmergencyDelete}
                disabled={!deleteReason.trim() || isSubmitting}
                className="btn btn--danger font-bold"
              >
                {isSubmitting ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}

      <MessageDialog {...dialogState} onClose={closeDialog} />
    </div>
  );
};

export default ShareHistory;
